import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, BookOpen, Video, FileText, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const EMPTY_LESSON = { title: "", description: "", video_url: "", notes_url: "", notes_text: "", order: 0 };
const EMPTY_COURSE = {
  title: "", description: "", thumbnail_url: "", level: "Beginner", duration: "",
  instructor_name: "", is_free: true, price: 0, is_active: true, lessons: [],
};

export default function AdminCourses() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/admin/courses");
    setItems(r.data || []);
  };

  const save = async () => {
    if (!editing.title) return toast.error("Title is required");
    try {
      const payload = {
        ...editing,
        price: editing.is_free ? 0 : Number(editing.price) || 0,
        lessons: (editing.lessons || []).map((l, i) => ({ ...l, order: i })),
      };
      if (editing.id) {
        await api.put(`/admin/courses/${editing.id}`, payload);
      } else {
        await api.post("/admin/courses", payload);
      }
      toast.success("Course saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this course? Enrolled students will lose access.")) return;
    await api.delete(`/admin/courses/${id}`);
    toast.success("Deleted");
    load();
  };

  const updateLesson = (idx, patch) => {
    const lessons = [...(editing.lessons || [])];
    lessons[idx] = { ...lessons[idx], ...patch };
    setEditing({ ...editing, lessons });
  };
  const addLesson = () => setEditing({ ...editing, lessons: [...(editing.lessons || []), { ...EMPTY_LESSON }] });
  const removeLesson = (idx) => setEditing({ ...editing, lessons: editing.lessons.filter((_, i) => i !== idx) });
  const moveLesson = (idx, dir) => {
    const lessons = [...editing.lessons];
    const j = idx + dir;
    if (j < 0 || j >= lessons.length) return;
    [lessons[idx], lessons[j]] = [lessons[j], lessons[idx]];
    setEditing({ ...editing, lessons });
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="heading text-2xl font-bold flex items-center gap-2"><BookOpen className="text-indigo-500" size={24} /> Courses</h1>
        <button onClick={() => setEditing({ ...EMPTY_COURSE })} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-1" data-testid="new-course-button">
          <Plus size={16} /> New Course
        </button>
      </div>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="p-10 rounded-xl border border-dashed border-slate-300 text-center col-span-full">
            <BookOpen size={36} className="mx-auto text-slate-300" />
            <div className="mt-3 text-slate-500">No courses yet. Create one to get started.</div>
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="p-5 rounded-xl border border-slate-200 bg-white" data-testid={`admin-course-${c.id}`}>
            {c.thumbnail_url && <img src={c.thumbnail_url} alt={c.title} className="w-full h-32 object-cover rounded-lg mb-3" />}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold">
              <span className="text-indigo-600">{c.level || "Beginner"}</span>
              {c.duration && <span className="text-slate-500">· {c.duration}</span>}
              {c.is_free
                ? <span className="text-emerald-600 ml-auto">FREE</span>
                : <span className="text-amber-600 ml-auto">₹{c.price}</span>}
            </div>
            <h3 className="heading text-lg font-bold mt-2">{c.title}</h3>
            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>
            <div className="mt-2 text-xs text-slate-500">{(c.lessons || []).length} lessons</div>
            <div className="mt-3 flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {c.is_active ? "Active" : "Inactive"}
              </span>
              <button onClick={() => setEditing(c)} className="ml-auto text-slate-700 hover:bg-slate-100 p-2 rounded" data-testid={`edit-course-${c.id}`}><Edit size={14} /></button>
              <button onClick={() => del(c.id)} className="text-red-600 hover:bg-red-50 p-2 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Create"} Course</DialogTitle>
            <DialogDescription>Add core details, then build the lesson list (video + notes per lesson).</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {/* Core details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Inp label="Title *" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} test="course-title-input" />
                <Inp label="Instructor Name" v={editing.instructor_name || ""} on={(v) => setEditing({ ...editing, instructor_name: v })} test="course-instructor-input" />
              </div>
              <Area label="Description *" v={editing.description} on={(v) => setEditing({ ...editing, description: v })} test="course-desc-input" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Sel label="Level" v={editing.level} options={LEVELS} on={(v) => setEditing({ ...editing, level: v })} test="course-level-select" />
                <Inp label="Duration" v={editing.duration || ""} on={(v) => setEditing({ ...editing, duration: v })} placeholder="e.g. 6 weeks" test="course-duration-input" />
                <Inp label="Thumbnail URL" v={editing.thumbnail_url || ""} on={(v) => setEditing({ ...editing, thumbnail_url: v })} placeholder="https://..." test="course-thumb-input" />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.is_free} onChange={(e) => setEditing({ ...editing, is_free: e.target.checked, price: e.target.checked ? 0 : editing.price })} className="accent-indigo-600" data-testid="course-is-free-checkbox" />
                  <span className="font-semibold">Free Course</span>
                </label>
                {!editing.is_free && (
                  <Inp label="Price (₹)" v={editing.price} on={(v) => setEditing({ ...editing, price: v })} placeholder="e.g. 499" test="course-price-input" />
                )}
                <label className="flex items-center gap-2 text-sm md:col-start-3">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-indigo-600" />
                  <span className="font-semibold">Active (visible publicly)</span>
                </label>
              </div>

              {/* Lessons */}
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-sm">Lessons ({(editing.lessons || []).length})</div>
                  <button type="button" onClick={addLesson} className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-bold px-3 py-1.5 rounded" data-testid="add-lesson-button">
                    + Add Lesson
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  {(editing.lessons || []).length === 0 && (
                    <div className="text-xs text-slate-400 text-center py-4">No lessons yet — add at least one.</div>
                  )}
                  {(editing.lessons || []).map((l, idx) => (
                    <div key={idx} className="p-3 rounded border border-slate-200 bg-white" data-testid={`lesson-card-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <GripVertical size={14} className="text-slate-300" />
                        <span className="text-xs font-bold text-slate-500">LESSON {idx + 1}</span>
                        <button type="button" onClick={() => moveLesson(idx, -1)} disabled={idx === 0} className="ml-auto text-xs px-2 py-0.5 rounded border disabled:opacity-30">↑</button>
                        <button type="button" onClick={() => moveLesson(idx, 1)} disabled={idx === editing.lessons.length - 1} className="text-xs px-2 py-0.5 rounded border disabled:opacity-30">↓</button>
                        <button type="button" onClick={() => removeLesson(idx)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`remove-lesson-${idx}`}><Trash2 size={12} /></button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input value={l.title} onChange={(e) => updateLesson(idx, { title: e.target.value })} placeholder="Lesson title" className="px-3 py-2 rounded border border-slate-300 text-sm" data-testid={`lesson-title-${idx}`} />
                        <input value={l.video_url || ""} onChange={(e) => updateLesson(idx, { video_url: e.target.value })} placeholder="Video URL (YouTube / Vimeo / .mp4)" className="px-3 py-2 rounded border border-slate-300 text-sm" data-testid={`lesson-video-${idx}`} />
                        <input value={l.notes_url || ""} onChange={(e) => updateLesson(idx, { notes_url: e.target.value })} placeholder="Notes URL (PDF / Drive)" className="px-3 py-2 rounded border border-slate-300 text-sm md:col-span-2" data-testid={`lesson-notes-url-${idx}`} />
                      </div>
                      <textarea rows={2} value={l.description || ""} onChange={(e) => updateLesson(idx, { description: e.target.value })} placeholder="Short description (optional)" className="mt-2 w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none" data-testid={`lesson-desc-${idx}`} />
                      <textarea rows={2} value={l.notes_text || ""} onChange={(e) => updateLesson(idx, { notes_text: e.target.value })} placeholder="Inline notes (optional plain text / markdown)" className="mt-2 w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none" data-testid={`lesson-notes-text-${idx}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancel</button>
            <button onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg px-5 py-2 text-sm" data-testid="course-save-button">
              Save Course
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Inp({ label, v, on, test, placeholder = "" }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input value={v} placeholder={placeholder} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 outline-none" data-testid={test} />
    </div>
  );
}
function Area({ label, v, on, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <textarea rows={3} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 outline-none resize-none" data-testid={test} />
    </div>
  );
}
function Sel({ label, v, options, on, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <select value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 outline-none" data-testid={test}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
